<template>
  <div class="international-projects-page">
    <div class="container">
      <!-- 页面标题 -->
      <div class="page-header">
        <h1 class="page-title">{{ $t('internationalProjects.title') }}</h1>
        <p class="page-subtitle">{{ $t('internationalProjects.subtitle') }}</p>
      </div>

      <!-- 搜索和筛选 -->
      <div class="search-section">
        <div class="card">
          <div class="search-container">
            <input
              v-model="searchKeyword"
              type="text"
              class="search-input"
              :placeholder="$t('internationalProjects.searchPlaceholder')"
              @input="handleSearch"
            />
            <div class="search-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM18 18l-4-4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Tab切换 -->
      <div class="tabs-section">
        <div class="card">
          <div class="tabs-nav">
            <button
              :class="['tab-button', { active: activeTab === 'projects' }]"
              @click="activeTab = 'projects'"
            >
              {{ $t('internationalProjects.projectsTitle') }}
              <span class="tab-count">({{ projectsCount }})</span>
            </button>
            <button
              :class="['tab-button', { active: activeTab === 'countries' }]"
              @click="activeTab = 'countries'"
            >
              {{ $t('internationalProjects.countriesTitle') }}
              <span class="tab-count">({{ countriesCount }})</span>
            </button>
          </div>
        </div>
      </div>

      <!-- 项目列表 -->
      <div class="content-section" v-if="activeTab === 'projects'">
        <div class="card">
          <div v-if="filteredProjects.length === 0" class="empty-state">
            <p class="empty-title">{{ $t('internationalProjects.noResults') }}</p>
            <p class="empty-desc">{{ $t('internationalProjects.noResultsDesc') }}</p>
          </div>
          <div v-else class="projects-grid">
            <div
              v-for="project in filteredProjects"
              :key="project.id"
              class="project-item"
              @click="handleProjectClick(project)"
            >
              <div class="project-icon">
                <span class="project-code">{{ project.id }}</span>
              </div>
              <div class="project-info">
                <div class="project-name">{{ project.name }}</div>
                <div class="project-type">{{ getProjectTypeName(project.type) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 国家列表 -->
      <div class="content-section" v-if="activeTab === 'countries'">
        <div class="card">
          <div v-if="filteredCountries.length === 0" class="empty-state">
            <p class="empty-title">{{ $t('internationalProjects.noResults') }}</p>
            <p class="empty-desc">{{ $t('internationalProjects.noResultsDesc') }}</p>
          </div>
          <div v-else class="countries-grid">
            <div
              v-for="country in filteredCountries"
              :key="country.countryId"
              class="country-item"
            >
              <div class="country-flag">
                <span class="country-code">{{ country.countryId }}</span>
              </div>
              <div class="country-info">
                <div class="country-name-zh">{{ country.nameCh }}</div>
                <div class="country-name-en">{{ country.nameEn }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { internationalProjects, searchProjects } from '@/data/international-projects'
import { countries, searchCountries } from '@/data/countries'

export default {
  name: 'InternationalProjects',
  setup() {
    const { t: $t } = useI18n()
    const router = useRouter()

    const activeTab = ref('projects')
    const searchKeyword = ref('')

    // 筛选后的项目列表
    const filteredProjects = computed(() => {
      const keyword = searchKeyword.value?.trim()
      if (!keyword) {
        return internationalProjects
      }
      return searchProjects(keyword)
    })

    // 筛选后的国家列表
    const filteredCountries = computed(() => {
      const keyword = searchKeyword.value?.trim()
      if (!keyword) {
        return countries
      }
      return searchCountries(keyword)
    })

    // 项目总数
    const projectsCount = computed(() => filteredProjects.value.length)

    // 国家总数
    const countriesCount = computed(() => filteredCountries.value.length)

    // 搜索处理
    const handleSearch = () => {
      // 搜索逻辑已经在 computed 中处理
    }

    // 获取项目类型名称
    const getProjectTypeName = (type) => {
      return $t(`internationalProjects.projectTypes.${type}`)
    }

    // 处理项目点击
    const handleProjectClick = (project) => {
      // 跳转到接收数据页面，携带项目名称参数
      router.push({
        name: 'ReceiveCode',
        query: {
          projectName: project.name
        }
      })
    }

    return {
      activeTab,
      searchKeyword,
      filteredProjects,
      filteredCountries,
      projectsCount,
      countriesCount,
      handleSearch,
      getProjectTypeName,
      handleProjectClick
    }
  }
}
</script>

<style scoped>
.international-projects-page {
  padding: 80px 0 60px;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.page-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.search-container {
  position: relative;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
  padding: 12px 48px 12px 20px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  font-size: 15px;
  background: var(--card-background);
  color: var(--text-primary);
  transition: all 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(68, 97, 242, 0.1);
}

.search-input::placeholder {
  color: var(--text-tertiary);
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
  pointer-events: none;
}

/* 空状态 */
.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.empty-desc {
  font-size: 14px;
  color: var(--text-secondary);
}

.tabs-section {
  margin-bottom: 30px;
}

.tabs-nav {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-color);
}

.tab-button {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.tab-button:hover {
  color: var(--primary-color);
  background: var(--hover-color);
}

.tab-button.active {
  color: var(--primary-color);
  font-weight: 600;
}

.tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--primary-color);
}

.tab-count {
  font-size: 14px;
  color: var(--text-tertiary);
  font-weight: 400;
}

.tab-button.active .tab-count {
  color: var(--primary-color);
}

.content-section {
  margin-bottom: 30px;
}


.countries-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--hover-color);
  border-radius: var(--radius-sm);
  transition: all 0.2s;
}

.country-item:hover {
  background: var(--card-background);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.country-flag {
  width: 48px;
  height: 48px;
  background: var(--primary-color);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 13px;
}

.country-code {
  font-size: 11px;
}

.country-info {
  flex: 1;
}

.country-name-zh {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  font-size: 15px;
}

.country-name-en {
  font-size: 12px;
  color: var(--text-secondary);
  text-transform: capitalize;
}

.projects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.project-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--hover-color);
  border-radius: var(--radius-sm);
  transition: all 0.2s;
  cursor: pointer;
}

.project-item:hover {
  background: var(--card-background);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.project-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #4461F2 0%, #38BFFF 100%);
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 11px;
}

.project-code {
  font-size: 10px;
  text-transform: uppercase;
}

.project-info {
  flex: 1;
}

.project-name {
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
  font-size: 15px;
}

.project-type {
  font-size: 12px;
  color: var(--text-secondary);
}


@media (max-width: 768px) {
  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 14px;
  }

  .tab-button {
    font-size: 14px;
    padding: 12px 16px;
  }

  .countries-grid,
  .projects-grid {
    grid-template-columns: 1fr;
  }

  .search-input {
    font-size: 14px;
  }
}
</style>
